// Routeur minimal, sans dépendance.
//
// POURQUOI PAS `react-router-dom` : à ce jour, TOUTES ses versions publiées
// portent au moins un avis de sécurité — la 7.18.x sur le mode RSC, les 6.0→7.17
// sur une dizaine d'autres (XSS, redirections ouvertes, DoS). L'essentiel vise
// des fonctions serveur qu'une application monopage n'utilise pas, mais
// « probablement pas exploitable chez nous » n'est pas un argument qu'on peut
// tenir devant un client dont on vend l'auditabilité. Nos besoins de navigation
// tiennent en quarante lignes : on les écrit.
//
// À reconsidérer le jour où une version sans avis existe, ou si le routage
// devient réellement complexe (chargements imbriqués, transitions).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/** Préfixe sous lequel l'application est servie (voir `vite.config.ts`).
 *  Vide : l'application est à la racine. La constante subsiste pour que le
 *  jour où elle repasserait sous un préfixe, il n'y ait qu'une ligne à changer
 *  — c'est déjà ce qui a permis de la ramener ici. */
const BASE: string = '';

/** Chemin courant, débarrassé du préfixe. Toujours commence par « / ». */
function currentPath(): string {
  const raw = window.location.pathname;
  const path = BASE && raw.startsWith(BASE) ? raw.slice(BASE.length) : raw;
  return path === '' ? '/' : path;
}

/** Adresse réelle d'un chemin interne, préfixe compris. À utiliser partout où
 *  l'on écrit un `href` à la main plutôt que d'employer `<Link>` — sinon le
 *  préfixe se retrouve codé en dur à plusieurs endroits, et le déplacer en
 *  oublie toujours un. */
export function hrefFor(to: string): string {
  return `${BASE}${to}`;
}

interface RouterValue {
  path: string;
  navigate: (to: string, options?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

export function RouterProvider({ children }: { children: ReactNode }) {
  const [path, setPath] = useState(currentPath);

  // Boutons précédent/suivant du navigateur : sans cet écouteur, l'URL change
  // mais l'écran reste — le défaut le plus signalé des routeurs maison.
  useEffect(() => {
    const onPop = () => setPath(currentPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to: string, options?: { replace?: boolean }) => {
    const url = hrefFor(to);
    if (options?.replace) window.history.replaceState(null, '', url);
    else window.history.pushState(null, '', url);
    setPath(to);
    // Une navigation doit ramener en haut : sinon on arrive au milieu d'un
    // écran parce que le précédent était défilé.
    window.scrollTo(0, 0);
  }, []);

  const value = useMemo(() => ({ path, navigate }), [path, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (!value) throw new Error('useRouter doit être utilisé dans un RouterProvider.');
  return value;
}

/**
 * Lien interne. Rend un vrai `<a href>` — donc ouvrable dans un nouvel onglet,
 * copiable, et annoncé comme un lien par les lecteurs d'écran. On n'intercepte
 * que le clic simple sans modificateur, pour ne pas casser Ctrl+clic.
 */
export function Link({
  to,
  children,
  className,
}: {
  to: string;
  children: ReactNode;
  className?: string;
}) {
  const { navigate } = useRouter();
  return (
    <a
      href={hrefFor(to)}
      className={className}
      onClick={(e) => {
        if (e.defaultPrevented || e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

/**
 * Extrait un paramètre d'un motif simple : `matchRoute('/projet/:id', path)`.
 * Renvoie `null` si le chemin ne correspond pas.
 */
export function matchRoute(pattern: string, path: string): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = path.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < patternParts.length; i += 1) {
    const p = patternParts[i];
    const v = pathParts[i];
    if (p === undefined || v === undefined) return null;
    if (p.startsWith(':')) params[p.slice(1)] = decodeURIComponent(v);
    else if (p !== v) return null;
  }
  return params;
}
